import { Description, Image } from '@mui/icons-material';
import { Box, CircularProgress, Container, Grid, Tab, Tabs, Typography } from '@mui/material';
import React, { cloneElement, lazy, Suspense, SyntheticEvent, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DownloadButtonContentProps, IDownloadData } from '../../../@types/Components';
import { EExportType } from '../../../@types/Enums';
import { IconIFC } from '../../../assets/icons';
import useResponsiveSizes from '../../../hooks/useResponsiveSizes';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { useExportContext } from '../../../provider/ExportProvider';
import { useLoadingContext } from '../../../provider/LoadingProvider';
import { useLoginContext } from '../../../provider/LoginProvider';
import { usePlan } from '../../../provider/PlanProvider';
import { SceneDataContext } from '../../../provider/SceneDataProvider';
import { useSelection } from '../../../provider/SelectionProvider';
import { SnackbarType, useSnackbar } from '../../../provider/SnackbarProvider';
import { getDownloadStyles } from '../../../utils/cssUtils';
import { exportToIFC } from '../../../utils/exportUtils';
import PanelButton from '../../common/PanelButton';

// Lazy load submodals to defer canvas creation until the modal is used.
const DownloadSubmodalPdf = lazy(() => import('./DownloadSubmodalPDF'));
const DownloadSubmodalImage = lazy(() => import('./DownloadSubmodalImage'));
const DownloadSubmodalIFC = lazy(() => import('./DownloadSubmodalIFC'));

/**
 * Extract HTTP status from common error shapes (fetch/axios/custom).
 */
const getHttpStatus = (error: unknown): number | undefined => {
  const e: any = error;
  return (
    e?.status ??
    e?.response?.status ?? // axios
    e?.cause?.status ??
    e?.data?.status
  );
};

/**
 * Creates a modal window for exporting and downloading the current plan from our floor planner scene.
 * Will provide three tabs containing the different export file type options.
 *
 * @returns {React.FC} functional component download modal window.
 */
const DownloadModal: React.FC = () => {
  const sizes = useResponsiveSizes();
  const [subtype, setSubtype] = useState<EExportType>(EExportType.NONE);
  const { isLoading, setIsLoading } = useLoadingContext(); // Use the global loading hook
  const { sceneRef } = useContext(SceneDataContext);
  const { building, exportPlan } = useBuilding();
  const { planModel } = usePlan();
  const { resetSelection } = useSelection();
  const { openLoginModal, refreshAccessToken } = useLoginContext();
  const { showSnackbar } = useSnackbar();
  const styles = getDownloadStyles(sizes);
  const { downloadData, setDownloadData } = useExportContext();
  const intl = useIntl();

  // UseEffect hook to only run once on initializing this component.
  useEffect(() => {
    // Utility method to create the typed download data with changed font sizes.
    const createDownloadData = (): IDownloadData => {
      const exportData = {
        scene: sceneRef,
        building: building,
        resetSelection: resetSelection,
        projectTitle: planModel.name === '' ? intl.formatMessage({ id: 'DownloadModal.unnamed_room' }) : planModel.name,
        fileName: planModel.name === '' ? intl.formatMessage({ id: 'DownloadModal.unnamed_room' }) : planModel.name,
        compassRotation: building.compass.rotation,
      };
      return exportData;
    };
    const tmpDownloadData = createDownloadData();
    setDownloadData(tmpDownloadData);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const handleButtonClick = async (type: EExportType): Promise<void> => {
    if (type === EExportType.IFC) {
      let activeToken: string | undefined;

      try {
        activeToken = await refreshAccessToken();
      } catch (error) {
        console.warn('Could not refresh Keycloak token before IFC download:', error);
      }

      if (!activeToken) {
        openLoginModal((newToken) => {
          if (newToken) {
            void handleButtonClick(type);
          }
        });
        return;
      }

      setIsLoading(true);
      try {
        await exportToIFC({
          planJsonString: exportPlan(),
          token: activeToken,
          fileName: planModel.name || 'floorplan',
        });
        showSnackbar(intl.formatMessage({ id: 'DownloadModal.download_success' }), SnackbarType.SUCCESS);
      } catch (error) {
        console.error('Error downloading IFC:', error);

        const status = getHttpStatus(error);
        const msg = String((error as any)?.message ?? '');

        const isInternalServerError = status === 500 || /internal server error/i.test(msg) || /\b500\b/.test(msg);

        if (isInternalServerError) {
          showSnackbar(
            intl.formatMessage({
              id: 'DownloadModal.ifc_export_failed',
              defaultMessage: 'Fehler beim Exportieren des Plans.',
            }),
            SnackbarType.ERROR,
          );
        } else {
          showSnackbar(
            `${intl.formatMessage({ id: 'MenuDrawer.maintenanceModeTitle' })}: ${intl.formatMessage({
              id: 'MenuDrawer.maintenanceModeMessage',
            })}`,
            SnackbarType.WARNING,
          );
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      setSubtype(type);
    }
  };

  const tabsData = useMemo(
    () => [
      {
        subtype: EExportType.PDF,
        label: 'pdf',
        content: () => <Suspense fallback={<div>Loading...</div>}>{downloadData !== null && <DownloadSubmodalPdf {...downloadData} setIsDownloading={setIsLoading} />}</Suspense>,
      },
      {
        subtype: EExportType.IMG,
        label: 'image',
        content: () => <Suspense fallback={<div>Loading...</div>}>{downloadData !== null && <DownloadSubmodalImage {...downloadData} setIsDownloading={setIsLoading} />}</Suspense>,
      },
      {
        subtype: EExportType.IFC,
        label: 'ifc',
        content: () => <Suspense fallback={<div>Loading...</div>}>{downloadData !== null && <DownloadSubmodalIFC {...downloadData} setIsDownloading={setIsLoading} />}</Suspense>,
      },
    ],
    [downloadData, setIsLoading],
  );

  const tabContent = useMemo(() => {
    const activeTab = tabsData.find((tab) => tab.subtype === subtype);
    return activeTab ? activeTab.content() : null;
  }, [subtype, tabsData]);

  const getTopBar = (): JSX.Element => {
    const handleTabChange = (_event: SyntheticEvent, newValue: EExportType): void => {
      setSubtype(newValue);
    };

    const tabs = tabsData.map((tab, id) => <Tab label={tab.label} key={id} value={tab.subtype} />);

    return (
      <Container sx={styles.submodal_topbar}>
        <Box component={'div'} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={subtype} onChange={handleTabChange} indicatorColor="secondary" textColor="secondary">
            {tabs}
          </Tabs>
        </Box>
      </Container>
    );
  };

  const getGridButtons = (): JSX.Element => {
    const buttonContent: DownloadButtonContentProps[] = [
      {
        id: 0,
        icon: <Description key="icon-0" />,
        buttonTypography: EExportType.PDF,
        labelId: 'DownloadModal.pdf',
        onClick: () => setSubtype(EExportType.PDF),
      },
      {
        id: 1,
        icon: <Image key="icon-1" />,
        buttonTypography: EExportType.IMG,
        labelId: 'DownloadModal.image',
        onClick: () => setSubtype(EExportType.IMG),
      },
      {
        id: 2,
        icon: <IconIFC key="icon-3" />,
        buttonTypography: EExportType.IFC,
        labelId: 'DownloadModal.ifc',
        onClick: () => handleButtonClick(EExportType.IFC), // direct download
        disabled: isLoading,
      },
    ];

    const renderButton = (button: DownloadButtonContentProps): JSX.Element => (
      <Grid key={`modalElement-${button.id}`} item xs={6} className="createNewModalElement" sx={styles.gridItem}>
        <PanelButton
          id={`panelBtn${button.id}`}
          innerContent={
            <Box component="div" sx={styles.iconBox}>
              {button.icons
                ? button.icons.map((icon, index) =>
                    cloneElement(icon, {
                      style: styles.icon,
                      key: index,
                    }),
                  )
                : cloneElement(button.icon!, { style: styles.icon })}
              <Typography sx={styles.typography_inButton}>{button.buttonTypography}</Typography>
              {button.buttonTypography === EExportType.IFC && isLoading && <CircularProgress size={24} sx={{ position: 'absolute', right: 8 }} />}
            </Box>
          }
          onClick={button.onClick}
          disabled={button.disabled}
        />
        <Typography noWrap sx={styles.typography}>
          <FormattedMessage id={button.labelId} />
        </Typography>
      </Grid>
    );

    return (
      <Grid container sx={styles.gridContainer}>
        {buttonContent.map(renderButton)}
      </Grid>
    );
  };

  const getDownloadSubmodal = (): JSX.Element => (
    <Container sx={styles.submodal_container}>
      {getTopBar()}
      {tabContent}
    </Container>
  );

  const modal = subtype === EExportType.NONE ? getGridButtons() : getDownloadSubmodal();

  return (
    <Box component={'div'} sx={styles.box}>
      <Typography variant="h3" sx={styles.headerTypography}>
        <FormattedMessage id="DownloadModal.header" />
      </Typography>
      {modal}
    </Box>
  );
};

export default DownloadModal;
